// ignore-string-externalization

import React from 'react';
import styled from 'styled-components';
import {
  body2FontSize,
  cta1LetterSpacing,
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  screenXsMax,
  screenLgMin,
  heading3FontSize,
  heading3LineHeight,
  spacer40,
  heading4FontSize,
  Type,
} from '@spotify-internal/encore-web';
import { IconArrowRight } from '@spotify-internal/encore-web-v6';
import {
  DELAYS,
  fadeInAnimation,
  withAnimationContext,
} from '@spotify-internal/frodor-modules';
import type { AnimationContextType } from '@spotify-internal/frodor-modules';
import { hexToRgba } from '../../../../features/src/color-conversion';
import { ModuleType } from '../types';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { ContentfulAsset } from '../../Contentful';
import {
  Box,
  ImageAspectBox1x1,
  ButtonTertiaryAsLink,
  Supertext,
  Link,
  CUSTOM_IPAD_WIDTH,
} from '../../ui';
import { formatText, fixHref, withoutProps } from '../../utils';
import { Colors } from '../../ui/colorsType';

const StyledSupertext = styled(Supertext)`
  padding-bottom: ${spacer16};
  font-size: ${body2FontSize};
  letter-spacing: ${cta1LetterSpacing};
`;

const Header = styled(Box)`
  background-color: ${(props: Colors) => props.backgroundColor};
  color: ${(props: Colors) => props.textColor};
  height: auto;
  display: flex;

  &.tallest {
    height: 100%;
  }

  @media (min-width: ${screenLgMin}) {
    justify-content: center;
    padding-bottom: ${spacer40};
  }
`;

const Content = styled.div`
  display: flex;
  flex-flow: row-reverse wrap;
  justify-content: space-between;
  width: 100%;

  @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
    flex-direction: column;
    justify-content: flex-start;
    padding-bottom: ${spacer32};
  }

  @media (min-width: ${screenLgMin}) {
    max-width: 1700px;
    justify-content: space-evenly;
  }
`;

const ImageContainer = styled(ImageAspectBox1x1)`
  width: 40%;
  display: flex;
  align-items: center;
  height: fit-content;
  align-self: center;
  max-height: 616px;
  max-width: 616px;

  /* account for nav bar, since the module starts underneath it */
  margin-top: 80px;
  @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
    margin-top: 40px;
    max-height: unset;
    align-self: start;
  }

  @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
    width: 100%;
    margin-bottom: ${spacer8};
  }
`;

const InnerText = styled.div`
  padding-left: 0px;
  width: 50%;
  margin-top: auto;

  /* account for nav bar, since the module starts underneath it */
  padding-top: 80px;
  @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
    padding-top: 40px;
  }

  margin-bottom: auto;

  @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
    width: 100%;
    margin: 0px;
  }

  @media (min-width: ${screenLgMin}) {
    width: 40%;
  }
`;

// TODO Remove hardcoding of font-size - there is no 56 in Encore, it is Frodor specific
const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    paddingBottom: '0.67em',
    variant: 'altoBrio',
    weight: 'bold',
  })<{ textColor: string; animationContext: AnimationContextType }>`
    ${fadeInAnimation(DELAYS.SECOND)};
    font-size: clamp(32px, 5vw, 94px);
    line-height: clamp(54px, 5vw, 104px);
    padding-bottom: ${spacer32};
    max-width: 684px;

    @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
      padding-top: ${spacer8};
      padding-bottom: ${spacer24};
      font-size: 48px;
      line-height: 54px;
    }

    @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
      text-align: left;
    }
  `,
);

const Subtext = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'cello',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-bottom: ${spacer32};
    font-size: ${heading3FontSize};
    line-height: ${heading3LineHeight};

    @media (max-width: 1600px) {
      font-size: ${heading4FontSize};
      line-height: ${heading3FontSize};
    }
  `,
);

const StyledLink = styled(withoutProps(Link, 'textColor'))<{
  textColor: string;
}>`
  color: ${({ textColor }) => textColor};
  padding-top: ${spacer32};

  &&:hover:not(:disabled),
  &&:focus:not(:disabled) {
    color: ${(props: Colors) => hexToRgba(props.textColor || '', 0.75)};
  }
`;

const FeaturedContentLink = withAnimationContext(
  styled(withoutProps(ButtonTertiaryAsLink, 'textColor'))<{
    textColor: string;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.FOURTH)};
    color: ${({ textColor }) => textColor};
    &&:hover:not(:disabled),
    &&:focus:not(:disabled) {
      color: ${({ textColor }) => hexToRgba(textColor || '', 0.75)};
    }

    margin-bottom: ${spacer32};
    @media (max-width: ${screenXsMax}) {
      margin-bottom: 0px;
    }

    svg {
      [dir='rtl'] & {
        transform: scaleX(-1);
      }
    }
  `,
);

const getHeadline = (
  ctaText: string,
  ctaLink: string,
  headline: string,
  textColor: string,
  trackCat: string,
) => {
  if (headline) {
    const HeadlineElement = (
      <Headline
        textColor={textColor}
        data-testid="header-module-headline"
        dangerouslySetInnerHTML={{ __html: formatText(headline) }}
      />
    );
    if (ctaText && ctaLink) {
      return (
        <StyledLink
          textColor={textColor}
          href={fixHref(ctaLink)}
          track={[trackCat, `CTA Link - ${ctaText}`]}
        >
          {HeadlineElement}
        </StyledLink>
      );
    }
    return HeadlineElement;
  }
  return <></>;
};

export type HeaderModuleType = ModuleType & {
  headline: string;
  subtext?: string;
  supertext?: string;
  image: ContentfulAsset;
  ctaLink?: string;
  ctaText?: string;
  backgroundColor: string;
  textColor: string;
};

const TRACK_CAT = 'module-heroHeader';

export const HeaderModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeaderModuleType
>(
  (
    {
      headline = '',
      subtext = '',
      supertext = '',
      image,
      ctaLink = '',
      ctaText = '',
      backgroundColor = '',
      textColor = '',
    },
    ref,
  ) => {
    return (
      <Header
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <Content>
          <ImageContainer>
            {image && <ContentfulImage asset={image} />}
          </ImageContainer>

          <InnerText>
            {supertext && <StyledSupertext>{supertext}</StyledSupertext>}
            {getHeadline(ctaText, ctaLink, headline, textColor, TRACK_CAT)}
            {subtext && (
              <Subtext
                dangerouslySetInnerHTML={{ __html: formatText(subtext) }}
              />
            )}
            {ctaText && ctaLink && (
              <FeaturedContentLink
                textColor={textColor}
                href={fixHref(ctaLink)}
                track={[TRACK_CAT, `CTA Link - ${ctaText}`]}
                iconTrailing={() => <IconArrowRight iconSize={16} />}
                condensedAll
              >
                {ctaText}
              </FeaturedContentLink>
            )}
          </InnerText>
        </Content>
      </Header>
    );
  },
);
